import { motion } from "framer-motion";
import RevealLines from "@/components/site/RevealLines";

const beliefs = [
  "Original stories with a meaningful purpose.",
  "Diverse characters and authentic perspectives.",
  "Independent authorship and creative ownership.",
  "Faith, imagination, love, justice, and redemption.",
  "Developing stories for books, film, television, and animation.",
  "Creating intellectual property that can live beyond one generation.",
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.19, 1, 0.22, 1] },
  },
};

export default function About() {
  return (
    <main className="pt-32 md:pt-40 pb-32 px-6 md:px-10">
      <div className="max-w-[1600px] mx-auto">
        <div className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--vv-bone-dim)] mb-6">
          — A. Lee Creations LLC · Manifesto
        </div>
        <h1 className="font-serif-vv text-6xl md:text-[10vw] leading-[0.9] tracking-tighter">
          <RevealLines
            lines={["The stories", "the world has", <em key="i">not experienced</em>, "yet."]}
          />
        </h1>

        {/* Body copy — company */}
        <div className="mt-24 grid grid-cols-12 gap-6 md:gap-16">
          <div className="col-span-12 md:col-span-4">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--vv-ember)]">
              — The Studio
            </div>
          </div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="col-span-12 md:col-span-8 space-y-8 max-w-3xl"
          >
            <p className="font-serif-vv text-2xl md:text-3xl leading-snug text-[color:var(--vv-bone)]/90">
              At A. Lee Creations LLC, we create original stories that inspire
              imagination, challenge perspectives, and leave a lasting
              impression.
            </p>
            <p className="text-lg leading-relaxed text-[color:var(--vv-bone)]/85">
              Founded by author{" "}
              <span className="text-[color:var(--vv-bone)] font-medium">
                Aquii Lee
              </span>
              , our independent creative company develops novels, books,
              screenplays, animation concepts, and digital media — across
              genres including romance, crime thrillers, biblical fiction,
              Christian literature, and inspirational storytelling.
            </p>
            <p className="text-lg leading-relaxed text-[color:var(--vv-bone)]/85">
              We believe creators should have the freedom to own their ideas,
              shape their vision, and build stories without sacrificing their
              originality. That is why every project begins with a clear
              purpose: to create memorable characters, meaningful worlds, and
              stories that connect with audiences from different cultures and
              backgrounds.
            </p>
          </motion.div>
        </div>

        {/* What We Believe */}
        <div className="mt-32 md:mt-40 grid grid-cols-12 gap-6 md:gap-16 border-t vv-line pt-16 md:pt-24">
          <div className="col-span-12 md:col-span-4">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--vv-ember)] mb-6">
              — What We Believe
            </div>
            <h2 className="font-serif-vv text-4xl md:text-6xl leading-[0.95] tracking-tight">
              What we
              <br />
              <em className="text-[color:var(--vv-bone-dim)]">stand for.</em>
            </h2>
          </div>

          <ul className="col-span-12 md:col-span-8 divide-y vv-line border-y vv-line">
            {beliefs.map((b, i) => (
              <motion.li
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.06 }}
                className="py-6 md:py-8 grid grid-cols-12 gap-4 items-baseline"
              >
                <span className="col-span-2 md:col-span-1 font-serif-vv text-2xl text-[color:var(--vv-ember)]">
                  ✦
                </span>
                <span className="col-span-10 md:col-span-11 font-serif-vv text-2xl md:text-3xl leading-tight">
                  {b}
                </span>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Closing statement */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-32 md:mt-40 border-t vv-line pt-16 md:pt-24 max-w-5xl"
        >
          <div className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--vv-ember)] mb-8">
            — In Closing
          </div>
          <p className="font-serif-vv text-4xl md:text-6xl leading-[1.05] tracking-tight">
            We are not here to repeat what has already been written.
          </p>
          <p className="mt-8 font-serif-vv italic text-3xl md:text-5xl leading-[1.05] tracking-tight text-[color:var(--vv-ember)]">
            We are here to create the stories the world has not experienced
            yet.
          </p>
        </motion.div>
      </div>
    </main>
  );
}
